import React, { useMemo, useContext, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Body1, Link } from '@fluentui/react-components';
import { MessageBar, MessageBarBody, MessageBarTitle } from '@fluentui/react-message-bar';

import { PropertyRow } from '@bingads-webui-campaign-react/ui-generator';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { ReactTemplate } from '@bingads-webui-react/react-template';
import { useWizardService, WIZARD_EVENTS } from '@bingads-webui-fluent-component/wizard';
import { ScopeContext } from '@bingads-webui-react/scope-service';

export const GoalDetails = ({ goals = [], hasLinkedInCompatibleGoals }) => {
  const i18n = useContext(I18nContext);
  const { scope } = useContext(ScopeContext);
  const { send } = useWizardService();

  const formattedGoals = useMemo(() => goals.map(goal => goal.name).join(', '), [goals]);

  const navigateToConversionGoals = useCallback(() => {
    send(WIZARD_EVENTS.UPDATE_WIZARD_DATA, { onExited: () => scope.save('/campaign/vnext/conversiongoals') });
    send(WIZARD_EVENTS.CANCEL);
  }, [send, scope]);

  const noGoalsSelectedWarningMessage = useMemo(() => {
    const template = i18n.getString(_TL_('Select {{bold}}Edit{{/bold}} to associate a conversion goal with this campaign.'));

    return (
      <ReactTemplate
        template={template}
        model={{ bold: text => <b>{text}</b> }}
      />
    );
  }, [i18n]);

  const noGoalsAvailableWarningMessage = useMemo(() => {
    const template = i18n.getString(_TL_('To add a conversion goal to this campaign, go to your conversion goals and create a new goal that’s compatible with LinkedIn. Once your campaign is created, you can go to your campaign settings and select the appropriate conversion goal below {{bold}}Conversions{{/bold}}. {{link}}Go to your conversion goals{{/link}}'));

    return (
      <ReactTemplate
        template={template}
        model={{
          bold: text => <b>{text}</b>,
          link: text => (
            // eslint-disable-next-line jsx-a11y/anchor-is-valid
            <Link as="a" onClick={navigateToConversionGoals}>
              {text}
            </Link>
          ),
        }}
      />
    );
  }, [i18n, navigateToConversionGoals]);

  const warningMessage = hasLinkedInCompatibleGoals
    ? noGoalsSelectedWarningMessage
    : noGoalsAvailableWarningMessage;

  return (
    <div>
      {goals.length > 0 && (
      <PropertyRow propertyLabel={{ label: _TL_('Goal name') }}>
        <Body1 block>{formattedGoals}</Body1>
      </PropertyRow>
      )}
      {
        goals.length === 0 && (
          <MessageBar intent="warning">
            <MessageBarBody>
              <MessageBarTitle>{i18n.getString(_TL_('No conversion goals selected.'))}</MessageBarTitle>
              {warningMessage}
            </MessageBarBody>
          </MessageBar>
        )
      }
    </div>

  );
};

GoalDetails.defaultProps = {
  goals: [],
  hasLinkedInCompatibleGoals: false,
};

GoalDetails.propTypes = {
  // eslint-disable-next-line
  goals: PropTypes.arrayOf(PropTypes.object),
  hasLinkedInCompatibleGoals: PropTypes.bool,
};

